/**
 * Profile — studio profile + personal details editor.
 *
 * Rebuilt to the design folder's iio.css visual language (no Mantine
 * cards / inputs). The design folder ships no `profile.html` page, so
 * this page composes the same primitives used everywhere else:
 *   .projects-header  — title + studio name
 *   hero card         — avatar tile + studio name + tagline +
 *                       "incomplete sections" hint
 *   sticky save bar   — appears when dirty, top of the scroll area
 *   .card grid        — 6 sections (Identity / You / Address /
 *                       Compliance / Banking / Social)
 *
 * Each section uses iio.css `.card` chrome and inline label/input
 * styles consistent with `CreateLead.tsx` (the existing form
 * template in this app).
 *
 * Save behaviour preserved verbatim:
 *   - Single Firestore `writeBatch` over `organizations/{orgId}`
 *     + `users/{uid}` so the customer sees a consistent post-save
 *     state.
 *   - Validation regexes (GSTIN / PAN / IFSC / email) unchanged.
 *   - Email + altEmail + altPhone mirrored to BOTH docs to keep
 *     the existing mobile-side read paths working.
 *
 * Out of scope (same as previous version):
 *   - Logo / cover photo upload (defer to R2 upload pipeline)
 *   - Phone number change (requires OTP re-auth — mobile only)
 */
import { notifications } from '@mantine/notifications';
import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { isAdminish } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { PagePlaceholder } from '@/components/PagePlaceholder';
import { db } from '@/lib/firebase';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useFullOrganization } from '@/lib/useFullOrganization';

// ───── Data shapes ─────

type OrgFields = {
  name?: string;
  tagline?: string;
  founded?: number | string;
  email?: string;
  altEmail?: string;
  altPhone?: string;
  website?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  gstin?: string;
  pan?: string;
  rera?: string;
  bankName?: string;
  bankAccount?: string;
  bankIFSC?: string;
  bankBranch?: string;
  upi?: string;
  instagram?: string;
  linkedin?: string;
};

type UserFields = {
  displayName?: string;
  email?: string;
  altEmail?: string;
  altPhone?: string;
  role?: string;
  photoURL?: string;
};

// ───── Validation ─────

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  return '';
}

function validate(org: OrgFields, user: UserFields): string | null {
  if (!org.name?.trim()) return 'Studio name is required.';
  if (user.email && !EMAIL_RE.test(user.email.trim()))
    return 'Email looks invalid.';
  if (user.altEmail && !EMAIL_RE.test(user.altEmail.trim()))
    return 'Accounts email looks invalid.';
  if (org.gstin && !GSTIN_RE.test(org.gstin.trim().toUpperCase()))
    return 'GSTIN should be 15 characters in the format 22AAAAA0000A1Z5.';
  if (org.pan && !PAN_RE.test(org.pan.trim().toUpperCase()))
    return 'PAN should be 10 characters in the format AAAAA9999A.';
  if (org.bankIFSC && !IFSC_RE.test(org.bankIFSC.trim().toUpperCase()))
    return 'IFSC should be 11 characters (e.g. HDFC0001234).';
  return null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
}

// ───── Inline form styles (matches CreateLead.tsx) ─────

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

const HINT: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-tertiary)',
  marginTop: 4,
};

const READONLY_PILL: React.CSSProperties = {
  ...INPUT,
  background: '#FAFAFA',
  color: 'var(--text-secondary)',
  cursor: 'not-allowed',
};

// ────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────

export function ProfilePage() {
  const { user } = useAuth();
  const { org, loading: orgLoading, error: orgError } = useFullOrganization();
  const { userDoc, loading: userLoading, error: userError } = useCurrentUser();

  // Role gate — only SA / Admin can edit the studio profile.
  // Mirrors the mobile gate at `app/(app)/profile.tsx` which uses
  // `can('studio.edit')`. Without this any Manager could change
  // the studio's GSTIN / PAN / bank details / address by hitting
  // /profile directly (sidebar already hides the link for non-
  // admins, but URL-hop bypassed the form-level check).
  const { role: orgRole } = useCurrentOrgRole();
  const canEditStudio = isAdminish(orgRole);

  const [orgForm, setOrgForm] = useState<OrgFields>({});
  const [userForm, setUserForm] = useState<UserFields>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate from snapshots (only when not mid-edit).
  useEffect(() => {
    if (!org || dirty) return;
    const d = org.data as OrgFields;
    setOrgForm({
      name: asString(d.name),
      tagline: asString(d.tagline),
      founded: asString(d.founded),
      email: asString(d.email),
      altEmail: asString(d.altEmail),
      altPhone: asString(d.altPhone),
      website: asString(d.website),
      addressLine1: asString(d.addressLine1),
      addressLine2: asString(d.addressLine2),
      city: asString(d.city),
      state: asString(d.state),
      pincode: asString(d.pincode),
      country: asString(d.country),
      gstin: asString(d.gstin),
      pan: asString(d.pan),
      rera: asString(d.rera),
      bankName: asString(d.bankName),
      bankAccount: asString(d.bankAccount),
      bankIFSC: asString(d.bankIFSC),
      bankBranch: asString(d.bankBranch),
      upi: asString(d.upi),
      instagram: asString(d.instagram),
      linkedin: asString(d.linkedin),
    });
  }, [org, dirty]);

  useEffect(() => {
    if (!userDoc || dirty) return;
    const d = userDoc.data as UserFields;
    setUserForm({
      displayName: asString(d.displayName),
      email: asString(d.email),
      altEmail: asString(d.altEmail),
      altPhone: asString(d.altPhone),
      role: asString(d.role),
      photoURL: asString(d.photoURL),
    });
  }, [userDoc, dirty]);

  const setOrgField =
    <K extends keyof OrgFields>(key: K) =>
    (value: string) => {
      setOrgForm((prev) => ({ ...prev, [key]: value }));
      setDirty(true);
    };
  const setUserField =
    <K extends keyof UserFields>(key: K) =>
    (value: string) => {
      setUserForm((prev) => ({ ...prev, [key]: value }));
      setDirty(true);
    };

  const onSave = async () => {
    if (!org || !user) return;
    const err = validate(orgForm, userForm);
    if (err) {
      notifications.show({
        title: 'Check the form',
        message: err,
        color: 'red',
      });
      return;
    }
    setSaving(true);
    try {
      const batch = writeBatch(db);

      const foundedNum =
        orgForm.founded && String(orgForm.founded).trim() !== ''
          ? Number(orgForm.founded)
          : null;

      const orgPayload: Record<string, unknown> = {
        name: (orgForm.name ?? '').trim(),
        tagline: (orgForm.tagline ?? '').trim(),
        founded:
          foundedNum && Number.isFinite(foundedNum) ? foundedNum : null,
        email: (orgForm.email ?? userForm.email ?? '').trim(),
        altEmail: (orgForm.altEmail ?? userForm.altEmail ?? '').trim(),
        altPhone: (orgForm.altPhone ?? userForm.altPhone ?? '').trim(),
        website: (orgForm.website ?? '').trim(),
        addressLine1: (orgForm.addressLine1 ?? '').trim(),
        addressLine2: (orgForm.addressLine2 ?? '').trim(),
        city: (orgForm.city ?? '').trim(),
        state: (orgForm.state ?? '').trim(),
        pincode: (orgForm.pincode ?? '').trim(),
        country: (orgForm.country ?? '').trim(),
        gstin: (orgForm.gstin ?? '').trim().toUpperCase(),
        pan: (orgForm.pan ?? '').trim().toUpperCase(),
        rera: (orgForm.rera ?? '').trim().toUpperCase(),
        bankName: (orgForm.bankName ?? '').trim(),
        bankAccount: (orgForm.bankAccount ?? '').trim(),
        bankIFSC: (orgForm.bankIFSC ?? '').trim().toUpperCase(),
        bankBranch: (orgForm.bankBranch ?? '').trim(),
        upi: (orgForm.upi ?? '').trim(),
        instagram: (orgForm.instagram ?? '').trim(),
        linkedin: (orgForm.linkedin ?? '').trim(),
        profileUpdatedAt: serverTimestamp(),
      };
      batch.set(doc(db, 'organizations', org.id), orgPayload, { merge: true });

      const userPayload: Record<string, unknown> = {
        displayName: (userForm.displayName ?? '').trim(),
        email: (userForm.email ?? '').trim(),
        altEmail: (userForm.altEmail ?? '').trim(),
        altPhone: (userForm.altPhone ?? '').trim(),
        role: (userForm.role ?? '').trim(),
        profileUpdatedAt: serverTimestamp(),
      };
      batch.set(doc(db, 'users', user.uid), userPayload, { merge: true });

      await batch.commit();
      setDirty(false);
      notifications.show({
        title: 'Profile saved',
        message:
          'Your studio details are updated. The next invoice will reflect the new GSTIN + address.',
        color: 'green',
        autoClose: 5000,
      });
    } catch (e) {
      notifications.show({
        title: 'Could not save profile',
        message:
          (e as Error).message
          || 'Please retry, or email hello@interioros.app if it persists.',
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    setDirty(false);
    // Re-hydrate by faking a re-snapshot — the useEffect blocks
    // hydration while dirty is true; clearing dirty triggers
    // re-hydration on the next render.
  };

  const verifiedPhone = user?.phoneNumber ?? '';

  const sectionsReady = useMemo(
    () => ({
      identity: Boolean(orgForm.name?.trim()),
      you: Boolean(userForm.displayName?.trim()),
      address: Boolean(orgForm.addressLine1?.trim() && orgForm.city?.trim()),
      compliance: Boolean(orgForm.gstin?.trim() || orgForm.pan?.trim()),
      banking: Boolean(orgForm.bankAccount?.trim() && orgForm.bankIFSC?.trim()),
      social: Boolean(orgForm.instagram?.trim() || orgForm.linkedin?.trim()),
    }),
    [orgForm, userForm.displayName],
  );

  const completedCount = Object.values(sectionsReady).filter(Boolean).length;
  const totalSections = Object.keys(sectionsReady).length;
  const allReady = completedCount === totalSections;

  // ── Loading / error / empty-org ─────────────────────────────
  const loading = orgLoading || userLoading;
  const error = orgError || userError;

  if (loading) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div style={{ padding: 64, textAlign: 'center', color: 'var(--text-secondary)' }}>
          Loading profile…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div
          className="card"
          style={{ padding: 24, color: 'var(--critical)', marginTop: 24 }}
        >
          Could not load profile: {error}
        </div>
      </div>
    );
  }

  if (!org || !user) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 48, textAlign: 'center', marginTop: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
            <i className="ph ph-user" />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No studio yet</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 360, margin: '0 auto' }}>
            You're signed in but haven't joined a studio. Open the mobile app to
            create or join one, then return here to edit profile details.
          </div>
        </div>
      </div>
    );
  }

  // Role gate. We render the placeholder *after* the org/user
  // hydration guards above so the early-render path doesn't flash
  // an access-denied screen during the initial load (orgRole is
  // null while the org doc resolves).
  if (!canEditStudio) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <PagePlaceholder
          title="Studio profile"
          body="Only the Super Admin or Admin can edit the studio profile. Ask your studio's owner if changes are needed here."
        />
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  return (
    <div className="page active" id="page-profile" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Studio Profile</h1>
          <div className="projects-stats">
            {completedCount} of {totalSections} sections complete
            {allReady ? ' · ✓ ready for invoicing' : ''}
          </div>
        </div>
      </div>

      {/* Sticky save bar — only when dirty */}
      {dirty ? (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <i
              className="ph-bold ph-pencil-simple"
              style={{ marginRight: 6, color: 'var(--warning)' }}
            />
            You have unsaved changes.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={onReset}
              disabled={saving}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      ) : null}

      {/* Hero — studio identity card */}
      <div
        className="card"
        style={{
          padding: 24,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: 16,
            background: 'var(--brand-bg)',
            color: 'var(--brand)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials(orgForm.name || org.data.name as string || '?')}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
            {orgForm.name || 'Untitled studio'}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              marginTop: 4,
            }}
          >
            {orgForm.tagline || 'Add a tagline to introduce your studio'}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-tertiary)',
              marginTop: 8,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            {orgForm.email ? (
              <span>
                <i className="ph ph-envelope" /> {orgForm.email}
              </span>
            ) : null}
            {verifiedPhone ? (
              <span>
                <i className="ph ph-phone" /> {verifiedPhone}
              </span>
            ) : null}
            {orgForm.website ? (
              <span>
                <i className="ph ph-globe" /> {orgForm.website}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* 6-section grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 16,
        }}
      >
        {/* ─── Identity ─── */}
        <Section
          title="Studio Identity"
          subtitle="Shown on invoices, the client view, and your public profile."
          ready={sectionsReady.identity}
          icon="ph ph-buildings"
        >
          <Row>
            <Field label="Studio name *" full>
              <input
                style={INPUT}
                value={orgForm.name ?? ''}
                onChange={(e) => setOrgField('name')(e.currentTarget.value)}
                placeholder="Praxis Design Studio"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Tagline" full>
              <input
                style={INPUT}
                value={orgForm.tagline ?? ''}
                onChange={(e) => setOrgField('tagline')(e.currentTarget.value)}
                placeholder="Considered interiors for considered lives."
                maxLength={120}
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="Founded">
              <input
                style={INPUT}
                value={String(orgForm.founded ?? '')}
                onChange={(e) => setOrgField('founded')(e.currentTarget.value)}
                inputMode="numeric"
                placeholder="2021"
              />
            </Field>
            <Field label="Website">
              <input
                style={INPUT}
                value={orgForm.website ?? ''}
                onChange={(e) => setOrgField('website')(e.currentTarget.value)}
                placeholder="praxis.studio"
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="Work email">
              <input
                style={INPUT}
                value={orgForm.email ?? ''}
                onChange={(e) => setOrgField('email')(e.currentTarget.value)}
                placeholder="studio@praxis.in"
                type="email"
              />
            </Field>
            <Field label="Accounts email">
              <input
                style={INPUT}
                value={orgForm.altEmail ?? ''}
                onChange={(e) => setOrgField('altEmail')(e.currentTarget.value)}
                placeholder="accounts@praxis.in"
                type="email"
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="Studio phone">
              <input
                style={INPUT}
                value={orgForm.altPhone ?? ''}
                onChange={(e) => setOrgField('altPhone')(e.currentTarget.value)}
                placeholder="+91 98765 43210"
                inputMode="tel"
              />
            </Field>
            <div />
          </Row>
        </Section>

        {/* ─── You ─── */}
        <Section
          title="You"
          subtitle="Your personal sign-in details. The phone number is your auth identity."
          ready={sectionsReady.you}
          icon="ph ph-user"
        >
          <Row cols={2}>
            <Field label="Display name">
              <input
                style={INPUT}
                value={userForm.displayName ?? ''}
                onChange={(e) =>
                  setUserField('displayName')(e.currentTarget.value)
                }
                placeholder="Ananya Prasad"
              />
            </Field>
            <Field label="Job title">
              <input
                style={INPUT}
                value={userForm.role ?? ''}
                onChange={(e) => setUserField('role')(e.currentTarget.value)}
                placeholder="Principal Designer"
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="Personal email">
              <input
                style={INPUT}
                value={userForm.email ?? ''}
                onChange={(e) => setUserField('email')(e.currentTarget.value)}
                placeholder="ananya@gmail.com"
                type="email"
              />
            </Field>
            <Field label="Personal phone">
              <input
                style={INPUT}
                value={userForm.altPhone ?? ''}
                onChange={(e) => setUserField('altPhone')(e.currentTarget.value)}
                placeholder="+91 98765 12345"
                inputMode="tel"
              />
            </Field>
          </Row>
          <Row>
            <Field
              label="Verified phone (sign-in)"
              hint="To change, use the mobile app's Change Phone flow."
              full
            >
              <input style={READONLY_PILL} value={verifiedPhone} readOnly />
            </Field>
          </Row>
        </Section>

        {/* ─── Address ─── */}
        <Section
          title="Address"
          subtitle="Used as the seller address on every GST invoice."
          ready={sectionsReady.address}
          icon="ph ph-map-pin"
        >
          <Row>
            <Field label="Address line 1" full>
              <input
                style={INPUT}
                value={orgForm.addressLine1 ?? ''}
                onChange={(e) =>
                  setOrgField('addressLine1')(e.currentTarget.value)
                }
                placeholder="Plot 14, Road 12"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Address line 2" full>
              <input
                style={INPUT}
                value={orgForm.addressLine2 ?? ''}
                onChange={(e) =>
                  setOrgField('addressLine2')(e.currentTarget.value)
                }
                placeholder="Banjara Hills"
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="City">
              <input
                style={INPUT}
                value={orgForm.city ?? ''}
                onChange={(e) => setOrgField('city')(e.currentTarget.value)}
                placeholder="Hyderabad"
              />
            </Field>
            <Field label="State">
              <input
                style={INPUT}
                value={orgForm.state ?? ''}
                onChange={(e) => setOrgField('state')(e.currentTarget.value)}
                placeholder="Telangana"
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="Pincode">
              <input
                style={INPUT}
                value={orgForm.pincode ?? ''}
                onChange={(e) => setOrgField('pincode')(e.currentTarget.value)}
                placeholder="500034"
                inputMode="numeric"
              />
            </Field>
            <Field label="Country">
              <input
                style={INPUT}
                value={orgForm.country ?? ''}
                onChange={(e) => setOrgField('country')(e.currentTarget.value)}
                placeholder="India"
              />
            </Field>
          </Row>
        </Section>

        {/* ─── Compliance ─── */}
        <Section
          title="Compliance"
          subtitle="GSTIN powers tax computation on every invoice. PAN + RERA are surfaced on quotations."
          ready={sectionsReady.compliance}
          icon="ph ph-file-text"
        >
          <Row>
            <Field
              label="GSTIN"
              hint="15 chars, e.g. 22AAAAA0000A1Z5"
              full
            >
              <input
                style={{ ...INPUT, textTransform: 'uppercase' }}
                value={orgForm.gstin ?? ''}
                onChange={(e) => setOrgField('gstin')(e.currentTarget.value)}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="PAN" hint="10 chars, e.g. AAAAA9999A">
              <input
                style={{ ...INPUT, textTransform: 'uppercase' }}
                value={orgForm.pan ?? ''}
                onChange={(e) => setOrgField('pan')(e.currentTarget.value)}
                placeholder="AAAAA9999A"
                maxLength={10}
              />
            </Field>
            <Field label="RERA" hint="State RERA registration number">
              <input
                style={{ ...INPUT, textTransform: 'uppercase' }}
                value={orgForm.rera ?? ''}
                onChange={(e) => setOrgField('rera')(e.currentTarget.value)}
                placeholder="A52100000123"
              />
            </Field>
          </Row>
        </Section>

        {/* ─── Banking ─── */}
        <Section
          title="Banking"
          subtitle="Printed on invoices so clients can settle by NEFT / IMPS / UPI."
          ready={sectionsReady.banking}
          icon="ph ph-bank"
        >
          <Row cols={2}>
            <Field label="Bank name">
              <input
                style={INPUT}
                value={orgForm.bankName ?? ''}
                onChange={(e) => setOrgField('bankName')(e.currentTarget.value)}
                placeholder="HDFC Bank"
              />
            </Field>
            <Field label="Branch">
              <input
                style={INPUT}
                value={orgForm.bankBranch ?? ''}
                onChange={(e) =>
                  setOrgField('bankBranch')(e.currentTarget.value)
                }
                placeholder="Banjara Hills"
              />
            </Field>
          </Row>
          <Row cols={2}>
            <Field label="Account number">
              <input
                style={INPUT}
                value={orgForm.bankAccount ?? ''}
                onChange={(e) =>
                  setOrgField('bankAccount')(e.currentTarget.value)
                }
                placeholder="50100123456789"
                inputMode="numeric"
              />
            </Field>
            <Field label="IFSC" hint="11 chars, e.g. HDFC0001234">
              <input
                style={{ ...INPUT, textTransform: 'uppercase' }}
                value={orgForm.bankIFSC ?? ''}
                onChange={(e) => setOrgField('bankIFSC')(e.currentTarget.value)}
                placeholder="HDFC0001234"
                maxLength={11}
              />
            </Field>
          </Row>
          <Row>
            <Field label="UPI ID" full>
              <input
                style={INPUT}
                value={orgForm.upi ?? ''}
                onChange={(e) => setOrgField('upi')(e.currentTarget.value)}
                placeholder="praxis@hdfc"
              />
            </Field>
          </Row>
        </Section>

        {/* ─── Social ─── */}
        <Section
          title="Social"
          subtitle="Optional. Shown on client-facing surfaces."
          ready={sectionsReady.social}
          icon="ph ph-share-network"
        >
          <Row cols={2}>
            <Field label="Instagram">
              <input
                style={INPUT}
                value={orgForm.instagram ?? ''}
                onChange={(e) =>
                  setOrgField('instagram')(e.currentTarget.value)
                }
                placeholder="@praxisstudio"
              />
            </Field>
            <Field label="LinkedIn">
              <input
                style={INPUT}
                value={orgForm.linkedin ?? ''}
                onChange={(e) => setOrgField('linkedin')(e.currentTarget.value)}
                placeholder="linkedin.com/company/praxisstudio"
              />
            </Field>
          </Row>
        </Section>
      </div>

      {/* Bottom save row (mirrors sticky bar so users at the end of
          the form don't need to scroll back up) */}
      {dirty ? (
        <div
          style={{
            marginTop: 24,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={onReset}
            disabled={saving}
          >
            Discard
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Section + Row + Field primitives
// ────────────────────────────────────────────────────────────

function Section({
  title,
  subtitle,
  ready,
  icon,
  children,
}: {
  title: string;
  subtitle: string;
  ready: boolean;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: 'var(--brand-bg)',
            color: 'var(--brand)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          <i className={icon} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 2,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {title}
            </div>
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: 12,
                background: ready ? 'var(--success-bg)' : '#F5F5F5',
                color: ready ? 'var(--success-text)' : 'var(--text-tertiary)',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              {ready ? 'Filled' : 'Incomplete'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            {subtitle}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Row({
  cols = 1,
  children,
}: {
  cols?: 1 | 2;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols === 2 ? '1fr 1fr' : '1fr',
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  hint,
  full,
  children,
}: {
  label: string;
  hint?: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <label style={LABEL}>{label}</label>
      {children}
      {hint ? <div style={HINT}>{hint}</div> : null}
    </div>
  );
}
